import type { Question } from '@trivia/shared';

/**
 * Scale — the flagship pack.
 *
 * Every question here is a quantity nobody memorises but most people can
 * reason toward from something they already know.
 */
export const scaleQuestions: Question[] = [
  {
    id: 'scale.blind-2020',
    type: 'numeric',
    prompt: 'Approximately how many people worldwide were estimated to be blind in 2020?',
    category: 'People',
    difficulty: 2,
    tags: ['health', 'population'],
    answer: 43_300_000,
    displayAnswer: 'about 43.3 million',
    unit: 'people',
    magnitude: 'millions',
    reveal: {
      headline: '43.3 million',
      explanation:
        'A global analysis put the number of blind people at roughly 43.3 million in 2020. Alongside them, close to 300 million more people were living with moderate or severe distance vision impairment.',
      comparison: 'That is a population somewhere between Spain and Argentina, all unable to see.',
    },
    source: {
      citation: 'GBD 2019 Blindness and Vision Impairment Collaborators',
      publisher: 'The Lancet Global Health',
      year: 2021,
      statisticYear: 2020,
      kind: 'estimated',
      volatile: true,
    },
  },
  {
    id: 'scale.ants',
    type: 'numeric',
    prompt: 'Approximately how many ants are alive on Earth right now?',
    category: 'Animals',
    difficulty: 3,
    tags: ['insects', 'biomass'],
    answer: 2e16,
    displayAnswer: '20 quadrillion',
    unit: 'ants',
    magnitude: 'astronomical',
    tolerance: 0.42,
    reveal: {
      headline: '20 quadrillion',
      explanation:
        'A 2022 synthesis of 489 field studies estimated about 20 quadrillion ants alive at any moment — 20,000,000,000,000,000 of them.',
      comparison: 'Roughly 2.5 million ants for every single human being.',
    },
    source: {
      citation: 'Schultheiss et al., "The abundance, biomass, and distribution of ants on Earth"',
      publisher: 'PNAS',
      year: 2022,
      kind: 'estimated',
    },
  },
  {
    id: 'scale.trees',
    type: 'numeric',
    prompt: 'Approximately how many trees are there on Earth?',
    category: 'Nature',
    difficulty: 2,
    tags: ['forests'],
    answer: 3.04e12,
    displayAnswer: 'about 3.04 trillion',
    unit: 'trees',
    magnitude: 'astronomical',
    tolerance: 0.36,
    reveal: {
      headline: '3.04 trillion',
      explanation:
        'A global mapping effort combining ground counts with satellite data landed on roughly 3.04 trillion trees — about eight times the previous best estimate.',
      comparison: 'Around 380 trees for every person alive. It was also about half the number before human civilisation.',
    },
    source: {
      citation: 'Crowther et al., "Mapping tree density at a global scale"',
      publisher: 'Nature',
      year: 2015,
      statisticYear: 2015,
      kind: 'estimated',
    },
  },
  {
    id: 'scale.cloud-water',
    type: 'numeric',
    prompt: 'A cumulus cloud one cubic kilometre in size holds roughly how much water, by mass?',
    subPrompt: 'Assume a typical fair-weather cumulus.',
    category: 'Science',
    difficulty: 3,
    tags: ['weather', 'physics'],
    answer: 500_000,
    displayAnswer: 'about 500,000 kg (500 tonnes)',
    unit: 'kg',
    magnitude: 'thousands',
    tolerance: 0.4,
    reveal: {
      headline: '500,000 kg',
      explanation:
        'At roughly half a gram of liquid water per cubic metre, a 1 km³ cumulus carries about 500 tonnes of water. It stays up because that water is divided into droplets so small that air resistance beats gravity.',
      comparison: 'About the weight of 100 elephants, floating silently over your head.',
    },
    source: {
      citation: 'Water Science School, "How much does a cloud weigh?"',
      publisher: 'U.S. Geological Survey',
      url: 'https://www.usgs.gov',
      kind: 'estimated',
      assumptions: ['Roughly 0.5 g of liquid water per cubic metre, across a volume of 1 km³.'],
    },
  },
  {
    id: 'scale.humans-ever-born',
    type: 'numeric',
    prompt: 'Approximately how many modern humans have ever been born?',
    category: 'People',
    difficulty: 3,
    tags: ['population', 'history'],
    answer: 1.17e11,
    displayAnswer: 'about 117 billion',
    unit: 'people',
    magnitude: 'billions',
    tolerance: 0.3,
    reveal: {
      headline: '117 billion',
      explanation:
        'Working back from archaeological population reconstructions, demographers estimate roughly 117 billion members of our species have ever been born.',
      comparison: 'With about 8 billion alive in 2022, nearly 7% of everyone who has ever lived is alive right now.',
    },
    source: {
      citation: 'Population Reference Bureau, "How Many People Have Ever Lived on Earth?"',
      publisher: 'Population Reference Bureau',
      year: 2022,
      statisticYear: 2022,
      url: 'https://www.prb.org',
      kind: 'modelled',
      assumptions: [
        'Counts births from about 190,000 BCE onward.',
        'Prehistoric population sizes and birth rates are reconstructed, not recorded.',
      ],
    },
  },
  {
    id: 'scale.chickens',
    type: 'numeric',
    prompt: 'How many chickens are alive on Earth at any given moment?',
    category: 'Animals',
    difficulty: 2,
    tags: ['farming', 'food'],
    answer: 2.6e10,
    displayAnswer: 'about 26 billion',
    unit: 'chickens',
    magnitude: 'billions',
    reveal: {
      headline: '26 billion',
      explanation:
        'Chickens are by far the most numerous bird on the planet, and the standing population is only part of the story: around 70 billion are slaughtered each year, so the flock turns over roughly three times annually.',
      comparison: 'Chickens outnumber humans by more than three to one.',
    },
    source: {
      citation: 'FAOSTAT livestock counts',
      publisher: 'UN Food and Agriculture Organization',
      statisticYear: 2022,
      kind: 'measured',
      volatile: true,
    },
  },
  {
    id: 'scale.grains-of-sand',
    type: 'numeric',
    prompt: 'Roughly how many grains of sand are there on all the beaches on Earth?',
    category: 'Earth',
    difficulty: 5,
    tags: ['wildcard'],
    answer: 7.5e18,
    displayAnswer: 'about 7.5 quintillion',
    unit: 'grains',
    magnitude: 'astronomical',
    tolerance: 0.65,
    reveal: {
      headline: '7.5 quintillion',
      explanation:
        'The most-cited estimate works from average beach dimensions and grain size to about 7.5 x 10^18 grains. Any answer like this is an order-of-magnitude exercise, not a count.',
      comparison: 'There are more stars in the observable universe than grains of sand on Earth — by a factor of thousands.',
    },
    source: {
      citation: 'University of Hawaii, School of Ocean and Earth Science and Technology estimate',
      kind: 'estimated',
      assumptions: ['Beach sand only. Desert and seabed sand would push the figure far higher.'],
    },
  },
  {
    id: 'scale.human-cells',
    type: 'numeric',
    prompt: 'How many cells are there in an adult human body?',
    category: 'Science',
    difficulty: 3,
    tags: ['biology'],
    answer: 3.6e13,
    displayAnswer: 'about 36 trillion',
    unit: 'cells',
    magnitude: 'astronomical',
    tolerance: 0.34,
    reveal: {
      headline: '36 trillion',
      explanation:
        'A 2023 census of the human body counted roughly 36 trillion cells in a 70 kg adult male. Red blood cells make up the large majority of that count while contributing only a few percent of body mass.',
      comparison: 'You are carrying more cells than there are stars in a hundred Milky Ways.',
    },
    source: {
      citation: 'Hatton et al., "The human cell count and size distribution"',
      publisher: 'PNAS',
      year: 2023,
      kind: 'estimated',
      assumptions: ['Reference adult male of about 70 kg.'],
    },
  },
  {
    id: 'scale.body-bacteria',
    type: 'numeric',
    prompt: 'How many bacteria live in and on a typical human body?',
    category: 'Science',
    difficulty: 3,
    tags: ['biology', 'microbiome'],
    answer: 3.8e13,
    displayAnswer: 'about 38 trillion',
    unit: 'bacteria',
    magnitude: 'astronomical',
    tolerance: 0.36,
    reveal: {
      headline: '38 trillion',
      explanation:
        'A careful 2016 recount put bacterial cells at roughly 38 trillion — close to a one-to-one ratio with human cells. The famous "ten bacteria for every human cell" claim traces back to a 1972 back-of-envelope estimate that was never rigorous.',
      comparison: 'Most of them live in your large intestine, and together they weigh about as much as your brain.',
    },
    source: {
      citation: 'Sender, Fuchs & Milo, "Revised estimates for the number of human and bacteria cells"',
      publisher: 'PLOS Biology',
      year: 2016,
      kind: 'estimated',
    },
  },
  {
    id: 'scale.blood-vessels',
    type: 'numeric',
    prompt: 'If you laid all the blood vessels in one adult human end to end, how long would they be?',
    category: 'Science',
    difficulty: 4,
    tags: ['biology', 'wildcard'],
    answer: 100_000,
    displayAnswer: 'about 100,000 km',
    unit: 'km',
    magnitude: 'thousands',
    tolerance: 0.42,
    reveal: {
      headline: '100,000 km',
      explanation:
        'Almost all of that length is capillaries — vessels so narrow that red blood cells squeeze through single file. The big arteries and veins you can name contribute almost nothing to the total.',
      comparison: 'Two and a half times around the equator, inside one person.',
    },
    source: {
      citation: 'Widely cited physiology estimate',
      kind: 'estimated',
      assumptions: ['Estimates in the literature range from roughly 60,000 to 100,000 km.'],
    },
  },
  {
    id: 'scale.plastic-ocean',
    type: 'numeric',
    prompt: 'Roughly how many individual pieces of plastic are floating in the world’s oceans?',
    category: 'Earth',
    difficulty: 3,
    tags: ['environment'],
    answer: 5.25e12,
    displayAnswer: 'about 5.25 trillion pieces',
    unit: 'pieces',
    magnitude: 'astronomical',
    tolerance: 0.45,
    reveal: {
      headline: '5.25 trillion pieces',
      explanation:
        'A global survey estimated 5.25 trillion floating plastic particles weighing about 269,000 tonnes. The count is dominated by tiny fragments; the mass is dominated by larger debris.',
      comparison: 'Trillions of pieces, but the total mass is only about 1,300 blue whales — most ocean plastic is unaccounted for.',
    },
    source: {
      citation: 'Eriksen et al., "Plastic Pollution in the World Oceans"',
      publisher: 'PLOS ONE',
      year: 2014,
      kind: 'estimated',
    },
  },
  {
    id: 'scale.trees-felled',
    type: 'numeric',
    prompt: 'Approximately how many trees are cut down worldwide each year?',
    category: 'Nature',
    difficulty: 3,
    tags: ['forests', 'environment'],
    answer: 1.5e10,
    displayAnswer: 'about 15 billion',
    unit: 'trees',
    magnitude: 'billions',
    tolerance: 0.34,
    reveal: {
      headline: '15 billion',
      explanation:
        'The same global tree census that counted 3 trillion trees also estimated that about 15 billion are felled each year, against roughly 5 billion replanted or regrown.',
      comparison: 'That is around 475 trees per second, every second.',
    },
    source: {
      citation: 'Crowther et al., "Mapping tree density at a global scale"',
      publisher: 'Nature',
      year: 2015,
      kind: 'estimated',
    },
  },
];
